import React, { useEffect, useState, useCallback } from "react";
import {
  Star,
  User,
  Mail,
  MessageSquare,
  Send,
  X,
  Plane,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import "./reviews.css";

// ---------------------------------------------------------------------------
// CONFIG
// ---------------------------------------------------------------------------
const REVIEWS_API = "https://www.broadwayholidays.co.uk/reviews_api.php";

// ---------------------------------------------------------------------------
// Star rating — display only
// ---------------------------------------------------------------------------
function StarRow({ rating, size = 16 }) {
  return (
    <div className="bh-star-row" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={n <= rating ? "bh-star bh-star-filled" : "bh-star"}
          strokeWidth={1.75}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Star rating — interactive picker for the write-review form
// ---------------------------------------------------------------------------
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const labels = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];
  return (
    <div className="bh-star-picker">
      <div className="bh-star-picker-row" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            className="bh-star-btn"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onFocus={() => setHover(n)}
            onBlur={() => setHover(0)}
            onClick={() => onChange(n)}
          >
            <Star
              size={32}
              strokeWidth={1.5}
              className={
                n <= (hover || value) ? "bh-star bh-star-filled" : "bh-star"
              }
            />
          </button>
        ))}
      </div>
      <div className="bh-star-picker-label">
        {labels[hover || value] || "Tap a star to rate"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A single review card, styled like a boarding-pass stub
// ---------------------------------------------------------------------------
function ReviewCard({ review }) {
  const initials = review.name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <article className="bh-review-card">
      <div className="bh-review-top">
        <div className="bh-avatar" aria-hidden="true">
          {initials}
        </div>
        <div className="bh-review-meta">
          <div className="bh-review-name-row">
            <span className="bh-review-name">{review.name}</span>
            {review.verified && (
              <span className="bh-verified" title="Verified traveller">
                <ShieldCheck size={14} strokeWidth={2} />
                Verified traveller
              </span>
            )}
          </div>
          <StarRow rating={review.rating} />
        </div>
        <Plane className="bh-stub-plane" size={18} strokeWidth={1.5} />
      </div>

      <div className="bh-stub-divider" role="presentation">
        <span className="bh-stub-hole bh-stub-hole-left" />
        <span className="bh-stub-dashes" />
        <span className="bh-stub-hole bh-stub-hole-right" />
      </div>

      <p className="bh-review-text">{review.text}</p>

      <div className="bh-review-date">{review.date}</div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Write-a-review modal
// ---------------------------------------------------------------------------
function WriteReviewModal({ open, onClose, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setRating(0);
      setText("");
      setName("");
      setEmail("");
      setStatus("idle");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = rating > 0 && text.trim().length >= 10 && name.trim() && validEmail;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(REVIEWS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rating,
          text: text.trim(),
          name: name.trim(),
          email: email.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Something went wrong. Please try again.");
      }
      setStatus("success");
      onSubmitted && onSubmitted(data.review);
    } catch (err) {
      setStatus("error");
      setError(err.message || "Couldn't send your review. Please try again.");
    }
  }

  return (
    <div className="bh-modal-backdrop" onMouseDown={onClose}>
      <div
        className="bh-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bh-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button className="bh-modal-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>

        {status === "success" ? (
          <div className="bh-modal-success">
            <CheckCircle2 size={40} strokeWidth={1.5} className="bh-success-icon" />
            <h3 id="bh-modal-title">Thanks for your review</h3>
            <p>It's now live on our reviews page. We appreciate you taking the time.</p>
            <button className="bh-btn bh-btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 id="bh-modal-title" className="bh-modal-title">
              Write a review
            </h3>
            <p className="bh-modal-sub">Tell other travellers about your trip with us.</p>

            <div className="bh-field">
              <label className="bh-label">Your rating</label>
              <StarPicker value={rating} onChange={setRating} />
            </div>

            <div className="bh-field">
              <label className="bh-label" htmlFor="bh-review-text">
                <MessageSquare size={15} strokeWidth={2} /> Your review
              </label>
              <textarea
                id="bh-review-text"
                className="bh-textarea"
                rows={5}
                maxLength={1200}
                placeholder="How was the booking process, the deal you found, and the service you received?"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div className="bh-char-count">{text.length}/1200</div>
            </div>

            <div className="bh-field-row">
              <div className="bh-field">
                <label className="bh-label" htmlFor="bh-review-name">
                  <User size={15} strokeWidth={2} /> Your name
                </label>
                <input
                  id="bh-review-name"
                  className="bh-input"
                  type="text"
                  placeholder="e.g. Sarah H."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="bh-field">
                <label className="bh-label" htmlFor="bh-review-email">
                  <Mail size={15} strokeWidth={2} /> Email address
                </label>
                <input
                  id="bh-review-email"
                  className="bh-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <p className="bh-privacy-note">
              Your email is used to verify your submission and is never shown publicly.
            </p>

            {status === "error" && (
              <div className="bh-form-error">
                <AlertCircle size={16} strokeWidth={2} />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="bh-btn bh-btn-primary bh-btn-block"
              disabled={!canSubmit || status === "sending"}
            >
              {status === "sending" ? (
                <>
                  <Loader2 size={18} className="bh-spin" /> Submitting…
                </>
              ) : (
                <>
                  <Send size={18} strokeWidth={2} /> Submit review
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Reviews section
// ---------------------------------------------------------------------------
export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterStar, setFilterStar] = useState(0);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetch(REVIEWS_API);
      const data = await res.json();
      setReviews(Array.isArray(data.reviews) ? data.reviews : []);
    } catch (err) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const total = reviews.length;
  const average = total
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / total).toFixed(1)
    : "0.0";

  const breakdown = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
    pct: total ? Math.round((reviews.filter((r) => r.rating === star).length / total) * 100) : 0,
  }));

  const filtered = filterStar
    ? reviews.filter((r) => r.rating === filterStar)
    : reviews;

  function handleSubmitted(newReview) {
    if (newReview) {
      setReviews((prev) => [newReview, ...prev]);
    } else {
      loadReviews();
    }
  }

  return (
    <section className="bh-reviews" id="reviews">
      <div className="bh-reviews-container">
        <div className="bh-reviews-logo-row">
          <img src="/logo.png" alt="Broadway Holidays" className="bh-reviews-logo" />
        </div>
        <div className="bh-reviews-header">
          <div>
            <span className="bh-eyebrow">Traveller feedback</span>
            <h2 className="bh-heading">What our customers say</h2>
            <p className="bh-subheading">
              Real feedback from people we've booked holidays for — the good deals, the easy
              process, and the service along the way.
            </p>
          </div>
          <button className="bh-btn bh-btn-gold" onClick={() => setModalOpen(true)}>
            <Star size={18} strokeWidth={2} /> Write a review
          </button>
        </div>

        <div className="bh-summary-card">
          <div className="bh-summary-score">
            <span className="bh-summary-number">{average}</span>
            <StarRow rating={Math.round(Number(average))} size={20} />
            <span className="bh-summary-count">{total.toLocaleString()} reviews</span>
          </div>
          <div className="bh-summary-bars">
            {breakdown.map(({ star, count, pct }) => (
              <button
                key={star}
                className={`bh-bar-row ${filterStar === star ? "bh-bar-row-active" : ""}`}
                onClick={() => setFilterStar(filterStar === star ? 0 : star)}
              >
                <span className="bh-bar-label">
                  {star} <Star size={12} className="bh-star bh-star-filled" strokeWidth={2} />
                </span>
                <span className="bh-bar-track">
                  <span className="bh-bar-fill" style={{ width: `${pct}%` }} />
                </span>
                <span className="bh-bar-count">{count}</span>
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="bh-state-row">
            <Loader2 size={22} className="bh-spin" /> Loading reviews…
          </div>
        )}

        {loadError && !loading && (
          <div className="bh-state-row bh-state-error">
            <AlertCircle size={20} /> Couldn't load reviews right now. Please refresh.
          </div>
        )}

        {!loading && !loadError && (
          <>
            <div className="bh-reviews-grid">
              {filtered.slice(0, visibleCount).map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="bh-state-row">No reviews with this rating yet.</div>
            )}

            {visibleCount < filtered.length && (
              <div className="bh-load-more-row">
                <button
                  className="bh-btn bh-btn-outline"
                  onClick={() => setVisibleCount((v) => v + 9)}
                >
                  Show more reviews
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <WriteReviewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={handleSubmitted}
      />
    </section>
  );
}